/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import axios, { AxiosError } from 'axios';
import _ from 'lodash';
import {
  ICompleteAccountDTO,
  ICreateAdminAccountDTO,
  ILoginDTO,
  IRequestErrorResponse,
  IReviewPolicyDTO,
  IUpdatePolicyDTO,
  ICreateTeamDTO,
  IAddSupportedVehicle,
  ICreateAgentDTO,
  IInitiateHubtelPaymentDTO,
  IVerifyMobilePaymentDTO,
  ICreateCommunityDTO,
  IAddCommunityContactDTO,
  IUpdateTeamDTO,
  IAddTeamPaymentDTO,
  IUpdateInsurerStoreDTO,
  IDownloadData,
  ISaveQuoteDTO,
  ITeamMetricsDTO,
  IRenewPolicyDTO,
  IClaimsPrelimQuestionsDTO,
  IClaimsAccidentCircumstanceDTO,
  IClaimsDriverParticularsDTO,
  IClaimsOtherVehicleInvolvedDTO,
  IClaimOtherAffectedPersonDTO,
  IClaimsPoliceInvolvementDTO,
  IClaimsOtherInformationDTO,
  IVehicleDamageFormData,
  IClaimsDeclarationDTO,
  IAddPartnerDTO,
  IUpdatePartnerQuotaDTO,
  IExportFuelSalesDataDTO,
  ICreateBlogPost,
  ICreateRNPLDTO,
  IPremiumApplicantDTO,
  IPremiumFinancingDTO,
  IBranch,
  ITeamNotification,
  completePayoutDTO
} from '../resources/interfaces';
import store, { RootState } from '../states';
import {
  ADMIN_PAYMENTS_STATUS_FILTER_OPTIONS,
  ADMIN_POLICY_STATUS_ACTIONS,
  CLAIM_STAGES,
  COMMUNITY_CHANNEL_OPTIONS,
  PAYMENTS_FILTER_OPTIONS,
  POLICY_DOCUMENT_TYPES,
  POLICY_REVIEW_STATES,
  RNPL_PAYMENT_CHANNEL,
  RNPL_REVIEW_STATES,
  COMPANY_TYPES
} from '../resources/enums';
import { IUpdateLead, IcreateLead } from '../models/lead/ILead';
import { ICreateInsurer } from '../models/insurer/IInsurer';
import { IgenerateDiscount } from '../models/discount/IDiscount';
import { SelectedDateRange } from '../components/dashboard/date-range-selector/datePicker';
import { IUpdateLeadTrail } from '../views/admin/leads/AddUpdate';
import { IAssignRelationshipManager } from '../views/partners/AssignRelationshipMagerModal';
import { IAssignPolicy } from '../views/shared/policies/auto/AssignPolicy';
import { ISetCommission } from '../views/partners/SetCommission';
import { IChangeAccessRight } from '../views/partners/ChangeAccessRightModal';
import { IPolicyReferee } from '../models/policy/IPolicy';
import { IVerifyPolicyRefereeOTP } from '../views/shared/policies/auto/PolicyEdit';
import { IChangePasswordDTO, IResetPasswordDTO } from '../resources/interfaces';
import { isDevelopment } from '../utils/miscFunctions';
import { IPayoutTransactionDetailsDTO } from '../views/partners/MarkAsPaidModal';
import { HIDE_TOP_LOADING, SESSION_EXPIRED, SHOW_TOP_LOADING } from '../states/slices/globalSlice';
import { ICreateTaskFormProps } from '../types';
import { SIGN_OUT } from '../states/slices/userAccountSlice';
import { IAssignMemberDTO } from '../views/partners/assignMemberModal';
import { IEditPaymentDTO } from '../views/shared/payments/PaymentEditModal';
import { IMarkPolicyAsDoneDTO } from '../views/shared/policies/auto/MarkPolicyAsDone';
import { ISales } from '../views/partners/insurers/insurerDetails/SalesTable';
import { RegisterFormData } from '../views/auth/Register';
import { ICreateRevenueTargetDTO } from '../views/shared/dashboard/shared/revenueTarget/CreateRevenueTarget';
import { IIntermediaryDTO } from '../views/shared/settings/Intermediaries/components/CreateIntermediary';
import { IRiskTypeInsuranceConfiguration } from '../views/shared/settings/insuranceConfiguration/AddRiskTypePage';
import { IInsuranceType } from '../views/shared/settings/insuranceConfiguration/AddInsuranceTypePage';
import { ICoverageTypeConfiguration } from '../views/shared/settings/insuranceConfiguration/AddProductModal';
import { IDiscountRate } from '../views/shared/settings/insuranceConfiguration/AddNoClaimDiscountModal';
import { ClaimsProps } from '../views/shared/policies/auto/claims';

export const apiUrl = process.env.REACT_APP_API;

// export const apiUrl = process.env.REACT_APP_STAGING_API;

export const api = axios.create({
  baseURL: `${apiUrl}/api`,
  timeout: isDevelopment() ? 200000 : 60000,
  withCredentials: true
});

export const sseApi = axios.create({
  baseURL: `${apiUrl}/`,
  timeout: isDevelopment() ? 200000 : 60000,
  withCredentials: true
});

// Add interceptors to instance
api.interceptors.response.use(
  (response) => {
    store.dispatch(HIDE_TOP_LOADING());
    return response;
  },
  (error: AxiosError) => {
    store.dispatch(HIDE_TOP_LOADING());
    const errorStruct: IRequestErrorResponse = {
      message: 'Error',
      status: 500
    };

    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    if (error.response) {
      const errorResponse = error.response;
      errorStruct.status = errorResponse.status;
      const data: Record<any, any> = <any>errorResponse.data;

      switch (errorResponse.status) {
        case 400: // Timeout
          errorStruct.message = 'Error';
          errorStruct.description = data?.detail;
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          break;
        case 401: // Unauthorized
          errorStruct.message = 'Unauthorized';
          errorStruct.description = data?.message ?? 'You are unauthorized to perform this operation.';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          store.dispatch(SESSION_EXPIRED(true));
          {
            const currentState = store.getState();
            if (currentState.userAccount.ACCOUNT.token) {
              store.dispatch(SIGN_OUT());
            }
          }
          break;
        case 403: // Forbidden
          errorStruct.message = 'Forbidden';
          errorStruct.description = data?.message ?? 'You cannot perform this action.';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          break;
        case 408: // Bad Request
          errorStruct.message = 'Connection Timeout';
          errorStruct.description = 'Connection timeout. Try again';
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          break;
        case 500: // Server Error
          errorStruct.message = 'Request Error';
          errorStruct.description = `${data?.message || 'An error occurred serving request. Try again later.'}`;
          // @ts-ignore
          errorStruct.detail = error.response?.data?.detail;
          break;
        default: // System Error
          errorStruct.message = 'System Error';
          break;
      }

      if (!errorStruct.description) errorStruct.description = data?.message;

      return Promise.reject(errorStruct);
    } else {
      // Something happened in setting up the request that triggered an Error
      return error;
    }
  }
);

api.interceptors.request.use((request) => {
  store.dispatch(SHOW_TOP_LOADING());
  // Set bearer token
  const storeState: RootState = store.getState();
  if (!_.isEmpty(storeState?.userAccount)) {
    const token = storeState?.userAccount?.ACCOUNT?.token;

    if (token && request && request.headers) {
      // @ts-ignore
      request.headers = {
        ...request.headers,
        Authorization: 'Bearer ' + token
      };
    }
  }

  return request;
});

// AUTH REQUESTS
export function loginWithPassword(payload: ILoginDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/password/signIn`, payload)
      .then((response: any) => {
        const token = response?.headers?.authorization?.split(' ')[1];
        response.data.data.token = token;
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

export function getAccountProfile(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/staff/account/getProfile`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function completeAccount(payload: ICompleteAccountDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/completeAccount`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response);
        }
        // else {
        //   reject(response);
        // }
      })
      .catch((error: AxiosError) => {
        reject(error);
        // reject(error?.response?.data);
      });
  });
}

export function resetAccountPAssword(admin: boolean, email: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/forgottenPassword?admin=${admin}&email=${email}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

// Change user password
export function changeStaffPassword(payload: IChangePasswordDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/staff/account/changePassword`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function createAdminAccount(payload: ICreateAdminAccountDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/teams/staff`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function registerNewAdmin(payload: RegisterFormData): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`auth/admin/register`, payload)
      .then((response: any) => {
        if (response.status === 200 || response.status === 201) {
          resolve(response?.data);
        } else {
          reject(response);
        }
      })
      .catch((error: AxiosError) => {
        reject(error?.response?.data);
      });
  });
}

export function signoutUser(accessRight?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/signOut?accessRights=${accessRight}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function sendRefereeOTP(phone: string, policyNumber: string | undefined): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/${policyNumber}/refereeOTP?phone=${phone}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Send Customer Phone OTP
export function sendCustomerOTP(id: number, phone: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/customers/${id}/request/otp?phone=${phone}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Send otp to any phone number
export function sendOTPToPhone(phone: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/resources/request/otp?phone=${phone}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Send Customer Email OTP
export function sendCustomerEmailOTP(email: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/requestOTP?email=${email}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Send Customer Email
export function sendCustomerEmail(mail: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/send/email`, mail)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// VerifyCustomerPhone
export function verifyPhoneOTP(id: number, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/customers/${id}/verify/otp`, payload)
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function verifyNonCustomerPhoneOTP(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/resources/verify/otp`, payload)
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Verify referee phone
export function adminPoliciesVerifyPhoneOTP(payload: IVerifyPolicyRefereeOTP): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/verify/phone/otp`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


// Reset user password
export function resetUserPassword(payload: IResetPasswordDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/auth/resetPassword`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Share referral code
export function shareReferral(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/staff/account/refer`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Verify Vehicle ID
export function verifyVehicle(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`services/vehicle/verify/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
export function searchForVehicle(model: string, year: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/vehicle/search/${encodeURIComponent(model)}/${year}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        console.log("search error", error);
        reject(error);
      });
  });
}
// Search Vehicle makes
export function searchVehicleMake(prefix: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`resources/vehicles/makes/search?prefix=${prefix}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        console.log("search error", error);
        reject(error);
      });
  });
}
// Search Vehicle models by make

// GET
// ​/api​/resources​/vehicles​/models
// getAllMakeModels
export function searchVehicleModelsByMake(make: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`resources/vehicles/models?make=${make}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        console.log("search error", error);
        reject(error);
      });
  });
}

//Verify Customer ID
export function verifyCustomerID(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/customers/${id}/verify/identity`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ENE AUTH REQUESTS

// *********************************************
// REQUEST TO CREATE LEADS

export function createLeads(formData: IcreateLead): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/leads/`, formData)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// REQUEST TO GET LEADS
export function getAllLeads(
  page: number,
  size: number,
  countryId: number,
  expireFrom?: string,
  expireTo?: string,
  createFrom?: string,
  createTo?: string,
  searchParam?: string,
  leadSource?: string,
  leadStatus?: string,
  staffId?: string,
  me?: boolean,
  tag?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/leads/?me=${me}&page=${page}&size=${size}&countryId=${countryId}${createFrom ? `&createFrom=${createFrom}` : ''
        }${createTo ? `&createTo=${createTo}` : ''}${expireFrom ? `&expireFrom=${expireFrom}` : ''
        }${expireTo ? `&expireTo=${expireTo}` : ''}${searchParam ? `&searchParam=${searchParam}` : ''
        }${leadSource ? `&leadSource=${leadSource}` : ''}${leadStatus ? `&leadStatus=${leadStatus}` : ''
        }${staffId ? `&staffId=${staffId}` : ''}${tag ? `&tag=${tag}` : ''}
      `
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// REQUEST TO GET LEADS
export function getAllLeadsSummaries(
  page: number,
  size: number,
  countryId: number,
  expireFrom?: string,
  expireTo?: string,
  createFrom?: string,
  createTo?: string,
  searchParam?: string,
  leadSource?: string,
  leadStatus?: string,
  staffId?: string,
  tag?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/leads/summary?countryId=${countryId}&page=${page}&size=${size}${createFrom ? `&createFrom=${createFrom}` : ''
        }${createTo ? `&createTo=${createTo}` : ''}${expireFrom ? `&expireFrom=${expireFrom}` : ''
        }${expireTo ? `&expireTo=${expireTo}` : ''}${searchParam ? `&searchParam=${searchParam}` : ''
        }${leadSource ? `&leadSource=${leadSource}` : ''}${leadStatus ? `&leadStatus=${leadStatus}` : ''
        }${staffId ? `&staffId=${staffId}` : ''}${tag ? `&tag=${tag}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// REQUEST TO GET A SINGLE LEAD
export function getSingleLead(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/leads/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// UPDATE LEAD
export function updateLead(id: string, payload: IUpdateLead): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/leads/${id}`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Assign LEAD
export function assignLead(id: string, adminEmail: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/leads/${id}/assignTo/${adminEmail}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// Lead trail update
export function updateLeadTrail(id: string, payload: IUpdateLeadTrail): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/leads/${id}/updates`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get Leads Quotes
export function getLeadsQuotes(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/leads/${id}/quotes`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// Admin create
export function adminCreateTask(eventType: string, id: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/tasks/${eventType}/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Admin update tasks]
export function updateTask(taskId: string, payload: ICreateTaskFormProps): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/tasks/${taskId}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all tasks
export function getAllTasks(
  page: number,
  size?: number,
  staffId?: string,
  from?: string,
  to?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/tasks/?page=${page}&size=${size}${staffId ? `&staff_id=${staffId}` : ''}${from ? `&from=${from}` : ''
        }${to ? `&to=${to}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Get my overdue tasks
export function getMyOverdueTasks(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/tasks/assigned?status=OVERDUE`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get single task
export function getTaskById(taskId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/tasks/${taskId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Comment on a task
export function addCommentToTask(taskId: string, comment: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/tasks/${taskId}`, comment)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Get Admins
export function getAdmins(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/leads/admins`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get adminAssignLead
export function adminAssignLead(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/leads/${id}/assignToSelf`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// *****************************************
// ALL POLICIES
export function getAllPoliciesOld(
  page: number,
  size: number,
  countryId: number,
  searchParam?: string,
  reviewStage?: string,
  expiredFrom?: string,
  expiredTo?: string,
  createFrom?: string,
  createTo?: string,
  startFrom?: string,
  startTo?: string,
  protectionType?: string,
  vehicleUsages?: string,
  leadSource?: string,
  managerId?: string,
  teamId?: string,
  insurerId?: number,
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/policies/?page=${page}&size=${size}&countryId=${countryId}${searchParam ? `&searchParam=${searchParam}` : ''
        }${reviewStage ? `&reviewStage=${reviewStage}` : ''}${expiredFrom ? `&expiredFrom=${expiredFrom}` : ''
        }${expiredTo ? `&expiredTo=${expiredTo}` : ''}${createFrom ? `&createFrom=${createFrom}` : ''
        }${createTo ? `&createTo=${createTo}` : ''}${startTo ? `&startTo=${startTo}` : ''}${startFrom ? `&startFrom=${startFrom}` : ''
        }${protectionType ? `&protectionType=${protectionType}` : ''}${vehicleUsages ? `&vehicleUsages=${vehicleUsages}` : ''
        }${leadSource ? `&leadSource=${leadSource}` : ''}${managerId ? `&managerId=${managerId}` : ''
        }${teamId ? `&teamId=${teamId}` : ''}${insurerId ? `&insurerId=${insurerId}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getAllPolicies(
  page: number,
  size: number,
  countryId: number,
  searchParam?: string,
  reviewStage?: string,
  expiredFrom?: string,
  expiredTo?: string,
  createFrom?: string,
  createTo?: string,
  startFrom?: string,
  startTo?: string,
  protectionType?: string,
  vehicleUsages?: string,
  leadSource?: string,
  managerId?: string,
  teamId?: string,
  insurerId?: number,
  branchId?: number,
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/policies/summary?page=${page}&size=${size}&countryId=${countryId}${searchParam ? `&searchParam=${searchParam}` : ''
        }${reviewStage ? `&reviewStage=${reviewStage}` : ''}${expiredFrom ? `&expiredFrom=${expiredFrom}` : ''
        }${expiredTo ? `&expiredTo=${expiredTo}` : ''}${createFrom ? `&createFrom=${createFrom}` : ''
        }${createTo ? `&createTo=${createTo}` : ''}${startTo ? `&startTo=${startTo}` : ''}${startFrom ? `&startFrom=${startFrom}` : ''
        }${protectionType ? `&protectionType=${protectionType}` : ''}${vehicleUsages ? `&vehicleUsages=${vehicleUsages}` : ''
        }${leadSource ? `&leadSource=${leadSource}` : ''}${managerId ? `&managerId=${managerId}` : ''
        }${teamId ? `&teamId=${teamId}` : ''}${insurerId ? `&insurerId=${insurerId}` : ''}${branchId ? `&branchId=${branchId}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getAllPoliciesV2(
  page: number,
  size: number,
  searchParam?: string,
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/policies/summary?page=${page}&size=${size}${searchParam ? `&searchParam=${searchParam}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Find all renewed policies
export function findRenewedPoliciesByPolicyNumber(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/find-renewed`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// importBulkPolicies
export function importBulkExistingPolicies(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/bulk-import`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// importBulkFleetPolicies
export function importBulkFleetPolicies(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/fleet/import`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create new policy
export function createPolicy(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/create`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create new policy
export function addPolicy(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/policies/add`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create new fleet policy
export function createFleetPolicy(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/fleet`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Complete fleet
export function completeFleet(fleetNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/policies/fleet/${fleetNumber}/complete`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get all fleet policies
export function getAllFleetPolicies(
  page: number,
  size: number,
  searchParam?: string,
  reviewStage?: string,
  expiredFrom?: string,
  expiredTo?: string,
  createFrom?: string,
  createTo?: string,
  startFrom?: string,
  startTo?: string,
  managerId?: string,
  teamId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/policies/fleets?page=${page}&size=${size}${searchParam ? `&searchParam=${searchParam}` : ''
        }${reviewStage ? `&reviewStage=${reviewStage}` : ''}${expiredFrom ? `&expiredFrom=${expiredFrom}` : ''
        }${expiredTo ? `&expiredTo=${expiredTo}` : ''}${createFrom ? `&createFrom=${createFrom}` : ''
        }${createTo ? `&createTo=${createTo}` : ''}${startTo ? `&startTo=${startTo}` : ''}${startFrom ? `&startFrom=${startFrom}` : ''
        }${managerId ? `&managerId=${managerId}` : ''}${teamId ? `&teamId=${teamId}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getFleetPolicyByFleetNumber(fleetNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/fleet/${fleetNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getAllPolicyRenewals(
  page: number,
  size: number,
  searchParam?: string,
  reviewStage?: string,
  expiredFrom?: string,
  expiredTo?: string,
  createFrom?: string,
  createTo?: string,
  startFrom?: string,
  startTo?: string,
  protectionType?: string,
  vehicleUsages?: string,
  leadSource?: string,
  managerId?: string,
  teamId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/policies/renewals?page=${page}&size=${size}${searchParam ? `&searchParam=${searchParam}` : ''
        }${reviewStage ? `&reviewStage=${reviewStage}` : ''}${expiredFrom ? `&expiredFrom=${expiredFrom}` : ''
        }${expiredTo ? `&expiredTo=${expiredTo}` : ''}${createFrom ? `&createFrom=${createFrom}` : ''
        }${createTo ? `&createTo=${createTo}` : ''}${startTo ? `&startTo=${startTo}` : ''}${startFrom ? `&startFrom=${startFrom}` : ''
        }${protectionType ? `&protectionType=${protectionType}` : ''}${vehicleUsages ? `&vehicleUsages=${vehicleUsages}` : ''
        }${leadSource ? `&leadSource=${leadSource}` : ''}${managerId ? `&managerId=${managerId}` : ''
        }${teamId ? `&teamId=${teamId}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getInsurerPoliciesById(
  id: string,
  page: number,
  size: number,
  searchParam?: string,
  reviewStage?: POLICY_REVIEW_STATES
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/insurers/insurer/${id}/policies?page=${page}&size=${size}${searchParam ? `&searchParam=${searchParam}` : ''
        }${reviewStage ? `&reviewStage=${reviewStage}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function updatePolicy(policyId: string, data: Partial<IUpdatePolicyDTO>): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/policies/${policyId}`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// SINGLE POLICY BY ID
export function getPolicy(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create a task on a policy
export function createTaskOnPolicy(policyNumber: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/${policyNumber}/task`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Apply loan on a policy
export function applyLoanToPolicy(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/policies/${policyNumber}/apply-loan`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function cancelPolicyRequest(policyId: string, reason: string, terminate: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/policies/${policyId}/cancel?reasons=${reason}&terminate=${terminate}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function approvePolicy(policyId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/policies/${policyId}/approve`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function deleteMotorPolicy(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/policies/${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function approveFleetPolicy(fleetNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/policies/fleet/${fleetNumber}/approve`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Save single policy other information
export function saveSinglePolicyOtherInformation(policyNumber: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/${policyNumber}/otherDetails`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Save fleet policy other information
export function saveFleetPolicyOtherInformation(fleetNumber: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/fleet/${fleetNumber}/otherDetails`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Save premium financing other information
export function savePremiumFinancingOtherInformation(applicationNumber: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/premium-financing/${applicationNumber}/otherDetails`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function changePolicyReviewState(policyId: string, payload: IReviewPolicyDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/${policyId}/review`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function changeFleetPolicyReviewState(fleetNumber: string, payload: IReviewPolicyDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/fleet/${fleetNumber}/review`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ADD POLICY UPDATE
export function addPolicyUpdate(policyId: string, payload: { category: string | null, description: string, pin: boolean, priority: string | null }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/${policyId}/updates`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ADD NON-MOTOR-POLICY UPDATE
export function addNonMotorPolicyUpdate(policyId: string, payload: { category: string | null, description: string, pin: boolean, priority: string | null }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/non-motor-policies/${policyId}/updates`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// EDIT POLICY UPDATE
export function editPolicyUpdate(policyId: string, id: number, payload: { category: string | null, description: string, pin: boolean, priority: string | null }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/policies/${policyId}/updates/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// EDIT NON MOTOR POLICY UPDATE
export function editNonMotorPolicyUpdate(policyId: string, id: number, payload: { category: string | null, description: string, pin: boolean, priority: string | null }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/non-motor-policies/${policyId}/updates/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// DELETE POLICY UPDATE
export function deletePolicyUpdate(policyNumber: string, id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/policies/${policyNumber}/updates/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


// DELETE NON-MOTOR POLICY UPDATE
export function deleteNonMotorPolicyUpdate(policyNumber: string, id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/non-motor-policies/${policyNumber}/updates/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ADD POLICY REFEREE
export function addPolicyReferee(policyId: string, payload: IPolicyReferee): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/${policyId}/addReferee`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function saveQuote(quoteData: ISaveQuoteDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/quote/vehicle/persist`, quoteData)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function createPolicyFromQuote(quoteId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/quotes/${quoteId}/generate/policy`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function renewPolicy(policyNumber: string, data: ISaveQuoteDTO, startDate?: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/quotes/${policyNumber}/renew?startsOn=${startDate}`, data)
      .then((response: any) => {
        if (response.status === 200) {
          resolve(response.data);
        }
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// LIST POLICIES BY CUSTOMER ID
export function getPoliciesByCustomer(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/customer/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// LIST FLEET POLICIES BY CUSTOMER ID
export function getFleetPoliciesByCustomer(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/fleet/customer/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// CHANGE POLICY STATUS
export function changePolicyStatus(policyState: string, id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/quotes/`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ASSIGN POLICY TO MANAGER
export function changePolicyManager(policyId: string, managerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/policies/${policyId}/assignTo/${managerId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Mark policy creation as complete
export function markPolicyCreationAsDone(
  policyNumber: string,
  payload: IMarkPolicyAsDoneDTO
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/policies/${policyNumber}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get all home insurancre policies
export function fetchAllHomePolicies(page: number, size: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/home/policies/?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getAdminTeamPolicies(page: number, size: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/policies/?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Run policy payment analysis
export function runPaymentAnalysisOnPolicy(policyNumber: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/analyse/payments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function sharePolicyAgreementDocumentLink(
  policyNumber: string,
  type: string,
  channel: string,
  identifier: string | number
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/document/${type}/share/${channel}/${identifier}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


export function sharePremiumFinancingAgreementDocumentLink(
  applicationNumber: string,
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/premium-financing/${applicationNumber}/resend-agreement-link`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function adminRequestFeedbackFromCustomer(
  policyNumber: string,
  channel: string,
  identifier: string | number
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/feedback/share/${channel}/${identifier}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function generateSaleForAPolicy(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/generate-sale`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function generatePolicyAgreementDocuments(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/services/policy/${policyNumber}/generateAgreementDocuments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// RELOAD POLICY VEHICLE PHOTOS
export function reloadPolicyVehiclePhotos(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/vehicle/photos/reload`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


// *************************************//

// GETTING ALL QUOTES
export function getAllQuotes(page: number, size: number, phone?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/quotes/?page=${page}&size=${size}${phone ? `&phone=${phone}` : ''}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// CREATE POLICY
export function checkPolicyQuote(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/v2/quotes/calculate`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// CREATE  fleet policy quote
export function checkFleetPolicyQuote(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/v2/quotes/fleet/calculate`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GETTING SINGLE QUOTE
export function getSingleQuote(ref: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/quotes/${ref}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GETTING ALL USER QUOTES
export function getAllUserQuotes(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/quotes/allUserQuotes`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// *********************************
// GET SINGLE CUSTOMER
export function getSingleCustomer(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/${id}`)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET SINGLE CUSTOMER
export function getCustomerVehiclesById(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/${id}/vehicles`)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET SINGLE CUSTOMER BY PHONE
export function getSingleCustomerByPhone(phone: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/phone/${phone}`)
      .then((response) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET SINGLE CUSTOMER BY SEARCHPARAM
export function getSingleCustomerBySearchParam(searchParam: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/find?searchParam=${searchParam}`)
      .then((response) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET CUSTOMER LIST
export function getAllCustomers(
  countryId: number,
  page?: number,
  size?: number,
  name?: string,
  elite?: boolean,
  stage?: string,
  createdFrom?: string,
  createdTo?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/customers/?countryId=${countryId}&page=${page}&size=${size}${name ? `&name=${name}` : ''}${stage ? `&stage=${stage}` : ''
        }${createdFrom ? `&createdFrom=${createdFrom}` : ''}${createdTo ? `&createdTo=${createdTo}` : ''}&elite=${elite}`
      )
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET ALL CUSTOMER LIST
export function getAllCustomersList(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        "/admin/customers/list"
      )
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET CUSTOMER VEHICLES
export function getCustomerVehicules(customerID: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/${customerID}/vehicles`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Edit Customer
export function updateCustomer(
  customerID: string,
  email: string,
  phone: string,
  otherPhone: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(
        `/admin/customers/${customerID}?email=${email}&phone=${phone}&otherPhone=${otherPhone}`
      )
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Edit Customer
export function updateEntireCustomer(customerID: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/customers/${customerID}`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Edit Customer
export function createNewCustomer(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/customers`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ADD CUSTOMER UPDATE
export function addCustomerUpdate(
  custmoreId: string,
  payload: { description: string }
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/customers/${custmoreId}`, payload)
      .then((response: any) => {
        resolve(response?.data?.content);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get All referrals
export function getAllReferrals(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/referrals/`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get Customer referrals
export function getCustomerReferrals(customerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/referrals/${customerId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Run customer kyc analysis
export function runKycAnalysisOnCustomer(customerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/${customerId}/analyse/kyc`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Run customer payment analysis
export function runPaymentAnalysisOnCustomer(customerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/customers/${customerId}/analyse/payments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// ****************************************/
// GENERATE DISCOUNT CODE

export function generateDiscountCode(discountData: IgenerateDiscount): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/discounts/`, discountData)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET ALL DISCOUNT CODE
export function getAllDiscounts(page: number, size: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/discounts/?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// DELETE DISCOUNT CODE
export function deleteDiscountCode(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/discounts/${id}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// CHANGE DISCOUNT CODE STATUS
export function changeDiscountCodeStatus(id: number, status: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/discounts/${id}/${status}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function applyDiscount(policyNumber: string, discountCode: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/discounts/${discountCode}/apply/${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//***************************** */

// Update  Insurer status

export function updateInsurerStatus(id: string, status: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/insurers/${id}/${status}`, status)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Update  Insurer DTO
export function updateInsurer(id: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/insurers/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// CREATE NEW INSURER
export function createNewInsurer(payload: ICreateInsurer): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/insurers/insurer`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// RETRIEVE SINGLE INSURER
export function getSingleInsurer(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/insurers/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getInsurerProfile(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/insurers/profile`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function checkInsurerSlugExists(slug: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/insurers/check/slug/${slug}`)
      .then((response: any) => {
        if (response.status === 200) {
          resolve(response?.data);
        } else {
          reject('Slug available');
        }
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


export function updateInsurerStore(data: IUpdateInsurerStoreDTO): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/insurers/store`, data)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET STAFF ACCOUNT PROFILE
export function getAdminAccounts(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/staffs`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getASingleTeam(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//SEND STAFF INVITE
export function sendStaffInviteLink(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/staffs/${id}/send-invite-link`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update admin account
export function updateAdminAccount(id: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/staff/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// INVITE INSURER AGENT /api/admin/insurers/inviteInsurerAgent
export function inviteInsurerUser(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/insurers/invite/user`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//GET RELATIONSHIP MANAGER TEAMS
export function getRelationshipManagerTeams(userId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/staff/${userId}/teamsManaging`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//GET RELATIONSHIP MANAGER
export function getRelationshipManager(managerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/staff/${managerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET ACCOUNT
export function getStaffAccountProfile(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/staff/account/getProfile`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET staff account by id
export function getStaffAccountById(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/staff/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET ALL TEAMS
export function getAllTeams(countryId?: number, page?: number, size?: number, companyTypes?: COMPANY_TYPES[]): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/?countryId=${countryId}&page=${page}&size=${size}${companyTypes?.length ? companyTypes.map(type => `&companyTypes=${type}`).join('') : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// CREATE TEAM
export function createTeam({ name, city, region, type, countryName }: ICreateTeamDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/teams/?name=${name}&city=${city}&region=${region}&type=${type}&countryName=${countryName}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function updateTeam(teamId: string, payload: IUpdateTeamDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/${teamId}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// ADD STAFF TO TEAM
export function assignMember({ id, staffEmail }: IAssignMemberDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/teams/${id}/${staffEmail}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
//Deactivate a company
export function activateDeactivateTeam(status: boolean, team_id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/${team_id}/activate/${status}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// DELETE ADMIN ACCOUNT
export function deleteAdminAccount(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/teams/${id}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// DISABLE ADMIN ACCOUNT
export function disableAdminAccount(id: string, status: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/teams/staff/${id}/activate/${status}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// UPDATE STAFF PROFILE
// export  function updateStaffProfile(id: string, payload: any) {
//   try {
//     const response = await api.patch(`/admin/teams/staffPersonal/${id}`, payload);
//     return response;
//   } catch (error) {
//     throw error;
//   }
// }

// UPDATE STAFF PROFILE
export function updateStaffProfile(id: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/teams/staffPersonal/${id}`, payload)
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get Team PROFILE
export function getTeamProfile(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/profile`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get Team staffs
export function getAllCompanyStaffById(id: number | string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/${Number(id)}/staffs`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


// Get Team staffs by branch id
export function getAllBranchStaffById(id: number | string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/branches/${Number(id)}/staffs`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
export function assignBrokerToPolicy(policyNumber: string, brokerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/assign-to-broker/${brokerId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// Get companies list by type
export function getAllCompaniesByType(types: COMPANY_TYPES[]): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/typeList?companyTypes=${types.join('&companyTypes=')}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get supervisor staffs
export function getSupervisorSurbodinates(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/staff/subordinates`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// CREATE AGENT ACCOUNT
export function addAgentsToMyTeam(payload: ICreateAgentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/teams/createAgentAccount`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// EDIT TEAM
export function editTeam(teamId: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/${teamId}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// INVITE MEMBER TO TEAM
export function inviteMemberToTeam(teamID: string, payload: ICreateAgentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/teams/${teamID}/inviteMember`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// INVITE AGENT TO TEAM
export function inviteAgent(payload: ICreateAgentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/teams/inviteAgent`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// DELETE AGENT FROM TEAM
export function deleteAgent(agentId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/teams/agent/${agentId}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get company onboarding status
export function getCompanyOnboardingStatus(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/onboarding-status`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//SET STAFF SUPERVISOR
export function getStaffActivities(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/staffs/activities`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//GET STAFF ACTIVITIES
export function setStaffSupervisor(staffId: number, managerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/staff/${staffId}/supervisor/${managerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//ASSIGN STAFF TO BRANCH
export function assignStaffToBranch(staffId: number, branchId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/staffs/${staffId}/branch/${branchId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//DELETE STAFF FROM BRANCH
export function removeStaffFromBranch(staffId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/staffs/${staffId}/branch`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//GET ACTIVE BRANCH LIST BY COMPANY ID
export function getActiveBranchesByCompanyId(companyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/branches/company/${companyId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}



// SET AGENCY PAYMENT ACCOUNT
export function setAgencyPaymentAccount(payload: IAddTeamPaymentDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/paymentAccount`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GET AGENCY PAYMENT ACCOUNT
export function getAgencyPaymentAccount(teamID: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payouts/companies/${teamID}/payment-accounts`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GENERATE AGENCY PAYOUT
export function generateAgencyPayout(teamID: string, reason: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/${teamID}/generatePayout?reason=${reason}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get policies in payout
export function generatePoliciesInPayout(payoutId: string, fileType: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/insurers/payouts/${payoutId}/download?fileType=${fileType}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function updateAgencyName(teamId: string, payload: { name: string }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/${teamId}/updateName`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// ASSIGN AGENCY MANAGER
export function assignAgencyManager({
  teamId,
  managerId
}: IAssignRelationshipManager): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/teams/${teamId}/assignAgencyAdmin/${managerId}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// ASSIGN RELATIONSHIP MANAGER
export function setRelationshipManager({
  teamId,
  managerId
}: IAssignRelationshipManager): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/teams/${teamId}/assignRelationshipManager/${managerId}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// CHANGE ADMIN ACCESS RIGHT
export function changeAccessRight({ staffID, accessRight }: IChangeAccessRight): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/staffs/${staffID}/accessRight?accessRight=${accessRight}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// ASSIGN COMMISSION RATE
export function setCommissionRate({ teamId, rate, type }: ISetCommission): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/teams/${teamId}/rate/${rate}?type=${type}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all commissions
export function getAllCommissions(page: number, size: number, companyId?: number, policyType?: string, insurerId?: number, searchParam?: string, from?: string, to?: string, paid?: string, offset?: number, paged?: boolean, sort?: string, unpaged?: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/commissions?page=${page}&size=${size}${companyId ? `&companyId=${companyId}` : ''}${policyType ? `&policyType=${policyType}` : ''}${insurerId ? `&insurerId=${insurerId}` : ''}${searchParam ? `&searchParam=${searchParam}` : ''}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}${page ? `&page=${page}` : ''}${paid ? `&paid=${paid}` : ''}${offset ? `&offset=${offset}` : ''}${paged ? `&paged=${paged}` : ''}${sort ? `&sort=${sort}` : ''}${unpaged ? `&unpaged=${unpaged}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


export function getCommissionsByApplicationNumber(applicationNumber?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/commissions/application/${applicationNumber}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// GET PAYOUT DATA FOR SINGLE TEAM
export function superGetInsurerPayoutsById(
  companyId: string,
  page: number,
  size: number,
  searchParam?: string,
  startDate?: string,
  endDate?: string,
  payoutType?: string,
  brokerId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/payouts?companyId=${companyId}&page=${page}&size=${size}${searchParam ? `&searchParam=${searchParam}` : ''
        }${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}${payoutType && `&payoutType=${payoutType}`}${brokerId && `&brokerId=${brokerId}`}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// GET PAYOUT DATA FOR SINGLE TEAM
export function getAgencyPayouts(teamId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/agency/${teamId}/payouts`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GET PAYOUT DATA FOR SINGLE TEAM
export function getTeamsPayouts(teamId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/${teamId}/payouts`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function getTeamSales(
  page: number,
  size: number,
  companyId: number,
  paid?: boolean,
  endDate?: string,
  searchParam?: string,
  startDate?: string,
): Promise<any> {

  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/sales/?page=${page}&size=${size}${companyId ? `&companyId=${companyId}` : ''
        }${searchParam ? `&searchParam=${searchParam}` : ''}${paid ? `&paid=${paid}` : ''
        }${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      })
  })
}
// GET SINGLE PAYOUT BY ID
export function getPayoutById(payoutId: string, teamId?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payouts/${payoutId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GET PAYOUT BY POLICY NUMBER
export function getPayoutByPolicyNumber(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payouts/policy-number/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// GET INSURER PAYOUT BY ID
export function getInsurerPayoutById(payoutId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payouts/${payoutId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// REQUEST FOR PAYOUT
export function requestPayout(teamId: string, payoutId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/payouts/companies/${teamId}/payouts/${payoutId}/review`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// REQUUEST FOR PAYOUT
export function markAsPaid(teamId: string, payoutId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/teams/agency/${teamId}/payouts/${payoutId}/paid`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GET AGENCY METRICS FOR SINGLE TEAM
export function getSingleAgencyTeamMetrics(teamId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/agency/${teamId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// approvePayout
export function approvePayout(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/payouts/${id}/approve`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// markPayoutAsPaid
export function markPayoutAsPaid(
  payoutId: string,
  teamId: string,
  payload: IPayoutTransactionDetailsDTO
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/payouts/companies/${teamId}/payouts/${payoutId}/paid`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Add insurer sales to unpaid payout
export function addInsurerSalesToUnpaidPayout(payoutId: string, salesIds: string[]): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/payouts/${payoutId}/add-sales`, salesIds)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// uploadInsurerPayoutEvidence
export function uploadInsurerPayoutEvidence(payoutId: string, file: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/settlements/${payoutId}/upload-payment-document`, file)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function uploadInsurerPayoutEvidenceDocument(payoutId: string, file: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/payouts/${payoutId}/upload-payment-document`, file)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GET TEAM PERFORMANCE
export function getSingleTeamPerformance(teamId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/${teamId}/performance`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function getTeamPerformanceMetrics(teamId: string, countryId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/${teamId}/performance/?countryId=${countryId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
//Get All staff
export function getAllAdminAccounts(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/staffs`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// GET
// ​/api​/admin​/teams​/staff​/{id}​/copy-invite-link
// copyStaffInviteLink
export function generateAgentLink(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/staffs/${id}/copy-invite-link`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


export function getStaffById(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/staffs/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}




{/* VEHICLES  */ }

// Create new vehicle
export function createVehicle(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/vehicle/create`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Pre-policy: request vehicle photos at the Vehicle Details stage of policy creation.
// Backend persists the vehicle (or reuses by chassis) and fires OffSure SMS keyed on VEH-{vehicleId}.
export function requestVehiclePhotosPrePolicy(payload: { vehicle: any; customerId: number }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/vehicle/photos/request`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create new vehicle
export function addVehicleToPolicy(policyNumber:string, payload: any): Promise<any> {

  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/policies/${policyNumber}/vehicles`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create new vehicle
export function updateVehicle(vehicleId: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/vehicle/update/${vehicleId}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Get all vehicles
export function getAllVehicles(page: number, size: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/vehicle/supported/getAll?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// Add supported vehicles
export function addVehicle(payload: IAddSupportedVehicle): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/vehicle/supported/add`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// get supported vehicle body types
export function getVehicleBodyTypes(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/services/get/vehicleBodyTypes`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// find vehicle by search param
export function findVehicleBySearchParam(searchParam: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/vehicle/find?searchParam=${searchParam}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}






// Dashboard
export function GetDashboardGenericsData(counrtyId: number, { from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/agency${from ? `?from=${from}` : ''}${to ? `&to=${to}` : ''}&counrtyId=${counrtyId}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function GetDashboardAgencyMetrics(counrtyId: number, teamId: string, { from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/agency/${teamId}${from ? `?from=${from}` : ''}${to ? `&to=${to}` : ''}?counrtyId=${counrtyId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function GetRelationshipManagerMetrics(countryId: number, managerId: string, { from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/admin/${managerId}/sales?countryId=${countryId}&from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getDashboardGenericsDataSuper(countryId: number, { from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/super?countryId=${countryId}&from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
export function getDashboardPayments() {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/payments`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getDashboardGraphDataSuper({ quarter, year }: { quarter: string; year: string }) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/super/graph?quarter=${quarter}&year=${year}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getDashboardMarketingMetrics(countryId: number, { from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/marketing?countryId=${countryId}&from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getDashboardGenericsDataAgent(countryId: number, { from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/agent${countryId ? `?countryId=${countryId}` : ''}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getDashboardGenericsGraphData(countryId: number, { quarter, year }: { quarter: string; year: string }) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/super/graph?quarter=${quarter}&year=${year}&countryId=${countryId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Company Statistics
export function getCompanyStatistics(countryId: number, from?: string, to?: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/companies?countryId=${countryId}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


export function getAllInitResources() {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/resources/get/web/init`)
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Upload documents and images
export function uploadDocument(url: string, data: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`${url}`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function uploadDocumentByPut(url: string, data: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`${url}`, data)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { });
  });
}

export function downloadPolicyDocument(documentId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/policies/document/${documentId}/download`, {
        responseType: 'blob'
      })
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function downloadInsurerPolicies(data: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(
        `admin/policies/export?from=${data?.from}&insurerId=${data?.insurerId}&status=${data?.status}&to=${data?.to}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Super download all or selected policies
export function downloadPolicies(data: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const params = new URLSearchParams();

    Object.entries(data || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    api
      .get(`admin/policies/download?${params.toString()}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Download policy details
export function downloadPolicyDetails(policyNumber?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`admin/policies/${policyNumber}/export`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function deleteUploadedImage(url: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .delete(`${url}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { reject(error) });
  });
}

// GET /api/admin/policies/{policyNumber}/blockers
// Returns list of { code, label, description } describing what's stopping the policy
// from moving to the insurer (Awaiting Payment, Missing KYC, Missing Vehicle Photos, …).
export interface PolicyBlocker {
  code: string;
  label: string;
  description: string;
}
export function fetchPolicyBlockers(policyNumber: string): Promise<PolicyBlocker[]> {
  return new Promise<PolicyBlocker[]>((resolve, reject) => {
    api
      .get(`admin/policies/${policyNumber}/blockers`)
      .then((response: any) => {
        resolve(response?.data?.data ?? []);
      })
      .catch((error: Error) => { reject(error) });
  });
}

// GET /api/admin/policies/{policyNumber}/vehicle-photos
export function requestPolicyVehiclePhotos(policyNumber: string, vehicleId: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`admin/policies/${policyNumber}/vehicle/${vehicleId}/photos`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { reject(error) });
  });
}

//Endorse an approved policy
export function endorsePolicy(policyNumber: string, payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`admin/policies/${policyNumber}/endorse`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => { reject(error) });
  });
}

// Get All Payments
export function getAllPayments(
  page: number,
  size: number,
  paymentStatus?: ADMIN_PAYMENTS_STATUS_FILTER_OPTIONS | 'All'
) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/payments/?page=${page}&size=${size}${paymentStatus ? `&paymentStatus=${paymentStatus}` : ''
        }`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


// Edit payment
export function editPolicyPayment(id: number, payload: IEditPaymentDTO, evidence: any) {
  return new Promise<any>((resolve, reject) => {
    api
      .put(
        `/admin/payments/${id}?bank=${payload.bank ? `${payload.bank}` : ''}&channel=${payload.channel
        }&dateOfPayment=${payload.dateOfPayment}&dueDate=${payload.dueDate}&narration=${payload.narration
        }&processor=${payload.processor}&reference=${payload.reference}&timeOfPayment=${payload.timeOfPayment
        }`,
        evidence
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get Payment link
export function getPaymentLink(id: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/${id}/generate-link`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Generate payment link for policy
export function generatePaymentLinkByPolicyNumber(policyNumber: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/policyNumber/${policyNumber}/generate-link`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get Payment link for selected payments
export function makeSelectedPaymentsForAPolicy(paymentIds: string[]) {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/payments/paySelected`, paymentIds)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get Payment link for bulk payment
export function makeBulkPaymentForPolicy(policyNumber: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/payBulk/${policyNumber}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Generate Payment Object
export function generatePolicyPaymentObjects(policyNumber: string, firstPaymentDate: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `admin/policies/${policyNumber}/generatePayments?${firstPaymentDate && `firstPaymentDate=${firstPaymentDate}`
        }`
      )
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Regenerate Payment Object
export function regeneratePolicyPaymentObjects(policyNumber: string, startOn: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `admin/policies/${policyNumber}/regeneratePayments?${startOn ? `startOn=${startOn}` : ''}`
      )
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Send payNow Notification
export function sendPayNowNotification(id: string, productType: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/payNowNotification/${id}/product-type/${productType}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getPayments(page: number, size: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payment/?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//SUPER APPROVE PAYMENT EDIT
export function superApprovePaymentEdit(id: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/payments/${id}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Reset single policy payment 
export function resetSinglePayment(id: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/${id}/reset`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//SUPER CANCEL PAYMENT APPROVAL
export function superCancelPaymentApproval(id: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/${id}/approval/cancel`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Get Insurer admin dashboard metrics
export function getInsurerDashboardMetrics({ from, to }: SelectedDateRange) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/insurer?from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Get admin dashboard metrics
export function getTeamDashboardMetrics(countryId: number, params: ITeamMetricsDTO, teamId?: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/metrics/team?countryId=${countryId}${teamId ? `&teamId=${teamId}` : ''}&from=${params.from}&to=${params.to}&year=${params.year}`)
      .then((response: any) => {
        resolve(response.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//fetchAllInsurerSales
export function getInsurerSales(
  page: number,
  size: number,
  searchParam?: string,
  startDate?: string,
  endDate?: string,
  insurerId?: string,
  paid?: boolean | string
) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/insurers/sales?page=${page}${searchParam ? `&searchParam=${searchParam}` : ''
        }&size=${size}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''
        }${insurerId ? `&insurerId=${insurerId}` : ''}${paid ? `&paid=${paid}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
interface policySalesList {
  salesNumbers: string[];
}

//Generate Payout
export function generatePayout(salesNumbers: policySalesList, teamId: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/insurers/${teamId}/payouts/generate`, salesNumbers)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//markInsurerPayoutAsPaid
export function markInsurerPayoutAsPaid(payoutId: string, payload: IPayoutTransactionDetailsDTO) {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/payouts/${payoutId}/paid`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//reviewInsurerPayout
export function reviewInsurerPayout(teamId: string, payoutId: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payouts/${payoutId}/review`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// fetchAllInsurerReviews
export function fetchAllInsurerReviews(page?: number, size?: number, searchParam?: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/insurers/insurer/reviews`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Super get insurer reviews by id
export function getInsurerReviewsbyId(id: number | string, page?: number, size?: number, searchParam?: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/insurers/insurer/${id}/reviews`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//fetchSingleInsurerReview
export function fetchSingleInsurerReview(id: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/insurers/insurer/${id}/review`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Comment on insurer review
export function commentOnInsurerReview(reviewID: number, payload: any) {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/insurers/insurer/${reviewID}/comment`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// getAllFileRequests
export function fetchInsurerFiles() {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/insurers/files`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getPendingPayments(page: number, size: number) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/pending?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getFilteredPayments(
  filterBy: PAYMENTS_FILTER_OPTIONS,
  countryId: number,
  name?: string,
  from?: string,
  to?: string
) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/payments/filter?filterBy=${filterBy}${countryId ? `&countryId=${countryId}` : ''}${name ? `&name=${name}` : ''}${from ? `&from=${from}` : ''
        }${to ? `&to=${to}` : ''}`
      )
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Initiate Hubtel Payment
export function initializeHubtelPayment(payload: IInitiateHubtelPaymentDTO): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/policies/recurringPayment`, payload)
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Initiate IT Consortium Payment
export function initializeItConsortiumPayment(
  momoNumber: string,
  policyNumber: string
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(
        `/services/itc/payment/installment?momoNumber=${momoNumber}&policyNumber=${policyNumber}`
      )
      .then((response: any) => {
        resolve(response.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function InitializeHubtelDirectCharge(paymentId: string): Promise<any> {
  return api.get(`services/hubtel/direct-charge?paymentId=${paymentId}`);
}

export function InitializeHubtelPreapproval(
  channel?: string,
  momoNumber?: string,
  policyNumber?: string
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(
        `services/hubtel/preapproval?channel=${channel}&msisdn=${momoNumber}&policyNumber=${policyNumber}`
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function hubtelPreapprovalVerify(otp?: string, policyNumber?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`services/hubtel/preapproval/verify?otp=${otp}&policyNumber=${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Create Online Fleet Payment
export function initializeOnlineFleetPayment(fleetNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/payments/online/fleet/${fleetNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Create Online Single Policy Payment
export function initializeOnlineSinglePolicyPayment(policyNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/payments/online/policy/${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Create Offline Fleet Payment
export function createOfflineFleetPayment(fleetNumber: string, payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/payments/offline/fleet/${fleetNumber}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Create Offline Single policy Payment
export function createOfflineSinglePolicyPayment(policyNumber: string, payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/payments/offline/policy/${policyNumber}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Activate or Deactivate Auto Debit
export function toggleAutoDebitStatus(active: boolean, applicationNumber: string, productType: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/payments/auto-billing?active=${active}&applicationNumber=${applicationNumber}&productType=${productType}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Check auto debit status
export function getAutoDebitStatus(applicationNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/payments/auto-billing-status?applicationNumber=${applicationNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function verifyPaymentOtp(payload: IVerifyMobilePaymentDTO): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/policies/verifyPaymentOtp`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function convertCurrency(have: string, want: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/resources/currency/convert?have=${have}&want=${want}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Financial Metrics on Payments page
export function getSuperPaymentMetrics(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/metrics/financials`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
///api/admin/payments/debit/{category}
export function initiateDebiting(category: PAYMENTS_FILTER_OPTIONS): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/payments/debit/${category}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function refreshMissedPayments(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/resources/schedulers/flagMissedPayments`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


export function refreshLongOverduePayments(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/resources/schedulers/flagLongOverduePayments`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function refreshOverduePayments(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/resources/schedulers/flagOverduePolicies`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}



// Financial Metrics on Payments page
export function getAdminPaymentStatistics(countryId: number, year?: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/payments/statistics?countryId=${countryId}${year ? `&year=${year}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// COMMUNITIES
export function createCommunity(payload: ICreateCommunityDTO): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/community/`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function updateCommunity(communityId: string, payload: ICreateCommunityDTO): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/community/${communityId}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function addCommunityContact(
  communityId: string,
  payload: IAddCommunityContactDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/community/${communityId}/contact`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function updateCommunityContact(
  communityId: string,
  contactId: string,
  payload: IAddCommunityContactDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/community/${communityId}/contact/${contactId}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getAllCommunities(page: number, size: number, name?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/community/all?page=${page}&size=${size}${name ? `&name=${name}` : ''}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getCommunityById(communityId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/community/${communityId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getCommunityPolicies(communityId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/community/${communityId}/policies`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function deleteCommunity(communityId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .delete(`/admin/community/${communityId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function notifyCommunity(
  communityId: string,
  channel: COMMUNITY_CHANNEL_OPTIONS,
  message: string
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/community/${communityId}/notify/${channel}?message=${message}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function deleteCommunityContact(communityId: string, contactId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .delete(`/admin/community/${communityId}/contact/${contactId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function addPoliciestoCommunity(communityId: string, policyNumbers: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/community/${communityId}/policies`, policyNumbers)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// END COMMUNITIES


// Get all claims
export function getAllClaims(
  page: number,
  size: number,
  countryId?: number,
  searchParam?: string,
  stage?: CLAIM_STAGES
): Promise<ClaimsProps> {
  return new Promise<ClaimsProps>((resolve, reject) => {
    api
      .get(
        `/admin/claims/?page=${page}&size=${size}&countryId=${countryId}${searchParam ? `&searchParam=${searchParam}` : ''
        }${stage ? `&stage=${stage}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get claim by id
export function getClaimById(claimNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/claims/${claimNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get claims for a policy
export function getPolicyClaims(policyId: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyId}/claims`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Super get all insurer claims
export function getInsurerClaimsById(
  id: string,
  page: number,
  size: number,
  searchParam?: string,
  stage?: CLAIM_STAGES
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(
        `/admin/insurers/insurer/${id}/claims/?page=${page}&size=${size}${searchParam ? `&searchParam=${searchParam}` : ''
        }${stage ? `&stage=${stage}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// Update claim prelim questions
export function updateClaimPrelimQuestions(
  claimNumber: string,
  payload: IClaimsPrelimQuestionsDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/claims/${claimNumber}/prelims/`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim accident Circumstancee
export function updateClaimAccidentCircumstance(
  claimNumber: string,
  payload: IClaimsAccidentCircumstanceDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/accidentCircumstance`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim claim vehicle damamge
export function updateClaimVehicleDamage(
  claimNumber: string,
  payload: IVehicleDamageFormData
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/vehicleDamage`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim claim driver details
export function updateClaimDriverParticulars(
  claimNumber: string,
  payload: IClaimsDriverParticularsDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/driverDetails`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim other vehicle involved
export function updateClaimOtherVehicleInvolved(
  claimNumber: string,
  payload: IClaimsOtherVehicleInvolvedDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/otherVehicleInformation`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim other affected persons
export function updateClaimOtherAffectedPersons(
  claimNumber: string,
  payload: IClaimOtherAffectedPersonDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/affectedPersons`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim claim police involvement
export function updateClaimPoliceInvolvement(
  claimNumber: string,
  payload: IClaimsPoliceInvolvementDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/policeInvolvement`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim  other information
export function updateClaimOtherInformation(
  claimNumber: string,
  payload: IClaimsOtherInformationDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/otherInformation`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Update claim  other information
export function updateClaimDeclaration(
  claimNumber: string,
  payload: IClaimsDeclarationDTO
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/agreement`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Update entire claim
export function updateEntireClaim(claimNumber: string, payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/claims/${claimNumber}/edit`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Export claim
export function exportClaimDetails(claimNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/claims/${claimNumber}/export`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Create new claim
export function createClaimFromPolicy(policyNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/claim`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


//Update claim stage
export function updateClaimStage(claimNumber: string, stage: CLAIM_STAGES): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/claims/${claimNumber}/review/${stage}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function archivePolicy(policyNumber: string, status: boolean): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .patch(`/admin/policies/${policyNumber}/archive/${status}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function resolveFinancials(policyNumber: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/policies/${policyNumber}/resolve-financials`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// START PRODUCTS
export function getAllProducts(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/products/`)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function addInsurerProducts(productIds: string[]): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .patch(`/admin/insurers/products?products=${productIds}`)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function addInsurerProductBenefits(benefits: string[]): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .patch(`/admin/insurers/benefits?benefits=${benefits}`)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getDownloadableFile(data: IDownloadData): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(
        `/admin/insurers/file-request?fileType=${data.fileType}&from=${data.from}&to=${data.to}&resourceType=${data.resourceType}&insurerId=${data.insurerId}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function insurerSubscribeToProduct(insurerId: number, add: boolean, productIds: string[]): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .patch(`/admin/insurers/${insurerId}/products?add=${add}&products=${productIds}`)
      .then((response: any) => {
        resolve(response?.data.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


// END PRODUCTS

// ROLES ENDPOINTS

//GET ALL ROLES
export function getAllRoles(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/settings/roles`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//GET SINGLE ROLE BY ID
export function getARoleByID(id: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/settings/role/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//GET ALL PERMISSIONS
export function getAllPermissions(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/settings/permissions`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//GET ALL PERMISSIONS BY COMPANY TYPE
export function getAllPermissionsByCompanyType(companyType: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`admin/settings/permission/${companyType}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


//GET ALL PERMISSIONS BY COMPANY TYPE
export function submitStaffPermissionsByStaffId(staffId: number, payload: { permissions: string[] }): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`admin/staffs/staff/${staffId}/permissions/bulk`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}


//Create Role
export function createRole(payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`/admin/settings/role`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// LOGS
//GET all audit logs/ logs
export function fetchAllLogs(page: number, size: number, staffId?: number, search?: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/logs/audit-trails/?page=${page}&size=${size}${staffId ? `&staffId=${staffId}` : ''}${search ? `&search=${search}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Get audit log by id
export function fetchSingleLogByID(id: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/logs/audit/${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Exports audit logs to a PDF file with optional search and staff filtering
export function exportAuditLogs(maxRecords: number, search?: string, staffId?: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`admin/logs/audit-trails/export/pdf?maxRecords=${maxRecords}${search ? `&search=${search}` : ''}${staffId ? `&staffId=${staffId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Setting MID credentials
export function setMIDCredentials(payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(
        `/admin/settings/mid-credentials/?branchCode=${payload.branchCode}&midApiKey=${payload.midApiKey}&midPassword=${payload.midPassword}&midReady=${payload.midReady}&midUsername=${payload.midUsername}&nicRegistrationNumber=${payload.nicRegistrationNumber}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Generate Insurer API Keys
export function generateInsurerAPIKeys(environment: 'PRODUCTION' | 'SANDBOX'): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`/admin/settings/generate/keys/?environment=${environment}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Insurer webhook settings PUT
export function insurerIntegrateWithWebhooks(payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .put(
        `/admin/settings/settings/webhook/?enable=${payload.enable}&enableForClaims=${payload.enableForClaims}&enableForPayouts=${payload.enableForPayouts}&enableForPolicies=${payload.enableForPolicies}&enableForSales=${payload.enableForSales}&webhookUri=${payload.webhookUri}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Insurer sales breakdown
export function insurerSalesBreakdown(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/metrics/insurer/sales`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Add product to insurer
export function addProduct(insurerId: string, add: boolean, products?: string[]): Promise<void> {
  const queryParams = `add=${add}${products ? `&products=${products.join('&products=')}` : ''}`;
  const url = `/admin/insurers/${insurerId}/products?${queryParams}`;

  return new Promise<void>((resolve, reject) => {
    api
      .patch(url)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Upload product icon
export function uploadProductIcon(productId: string, file: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .patch(`admin/products/${productId}/icon`, file)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Create new product
export function createNewProduct(payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`admin/products/`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Change product status
export function changeProductStatus(productId: string, status: boolean): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .patch(`admin/products/${productId}/activate/${status}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function showProtectedData(dataId: string, typeOfData: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`/admin/staff/account/reveal-protected-data?dataId=${dataId}&typeOfData=${typeOfData}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function addSlackWorkspace(payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`admin/settings/slack/workspace`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function addTeamToSlackWorkspace(payload: any): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`admin/settings/slack/workspace`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getSlackWorkspaces(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`admin/settings/slack/workspaces`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function getAllApprovalsForSuperAdmin(stage: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    api
      .get(`admin/approvals/?stage=${stage}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Clear "Me" notifications GET
export function clearMeNotifications(): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    sseApi
      .get(`sse/admin/notifications/clear`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// CREATE Partner
export function createPartner(payload: IAddPartnerDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/partners`, payload)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// Get all Partners
export function getAllPartners(page?: number, size?: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/partners/?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get a partner
export function getAPartner(partnerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/partners/${partnerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get team to partner
export function addTeamToPartner(teamId: string, partnerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/teams/${teamId}/partner/${partnerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
// Get partner teams
export function getPartnerTeams(partnerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/partners/${partnerId}/teams`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Upload partner logo
export function uploadPartnerLogo(partnerId: string, logo: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/partners/logo/upload?id=${partnerId}`, logo)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Fetch Yango parks
export function getYangoParks(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/yango/parks`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//
export function addFuelPurchase(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/fuels/purchases`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//
export function getAllFuelPurchases(
  page: number,
  size: number,
  from?: string,
  to?: string,
  partnerId?: string,
  stationId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/fuels/purchases?page=${page}&size=${size}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''
        }${stationId ? `&stationId=${stationId}` : ''}${partnerId ? `&partnerId=${partnerId}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//
export function getPartnerFuelMetrics(
  from?: string,
  to?: string,
  partnerId?: string,
  stationId?: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/metrics/fuel/sales?${from ? `from=${from}` : ''}${to ? `&to=${to}` : ''}${partnerId ? `&partnerId=${partnerId}` : ''
        }${stationId ? `&stationId=${stationId}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}
//
export function getAllFuelRebates(
  page: number,
  size: number,
  from: string,
  to: string
): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/fuels/rebates?page=${page}&size=${size}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''
        }`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get partner fuel quota
export function getPartnerFuelQuota(partnerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/quota?partnerId=${partnerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Update partner fuel quota
export function updatePartnerFuelQuota(payload: IUpdatePartnerQuotaDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/fuels/quota`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get fuel vendor sales history
export function getFuelVendorSalesHistory(staffId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sales/history?staff_id=${staffId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// vendor close daily sales
export function closeFuelVendorDailySales(date?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/close-day-sale`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// get unclosed vendor daily sales
export function getUnclosedFuelVendorDailySales(status: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sales${status ? `?status=${status}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Fuel admin approve vendor daily sales
export function fuelAdminCloseVendorSales(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/close-staff-sale?id=${id}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Export fuel partner sales
export function exportFuelPartnersales({
  from,
  stationId,
  to
}: IExportFuelSalesDataDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `/admin/fuels/transactions/export${from ? `?from=${from}` : ''}${stationId ? `&stationId=${stationId}` : ''
        }${to ? `&to=${to}` : ''}`
      )
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// getAllFuelSaleTransactions
export function getAllFuelSaleTransactions(saleId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sale/${saleId}/transactions`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// grt fuel Settlements
export function getAllFuelSettlements(completed: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sales/settlements?completed=${completed}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//  GET settle Station Non Cash Sale

export function settleStationNonCashSale(id: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sales/${id}/approve-settlement`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//  request NonCashSaleSettlement
export function requestNonCashSaleSettlement(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sales/request-settlement`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get unsettled fuel balance
export function getUnsettledFuelBalance(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/fuels/sales/unsettled-balance`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

{
  /* BLOGS */
}

//Create a blog post
export function createblogPost(payload: ICreateBlogPost): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/blog/post`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//get a blog post
export function getblogPostById(blogId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/blog/post/${blogId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//get blog posts
export function getAllBlogPosts(page: number, published: boolean, size: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/blog/posts?page=${page}&published=${published}&size=${size}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Update blog posts
export function updateBlogPostById(blogId: number, payload: ICreateBlogPost): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/blog/post/${blogId}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Delete blog posts
export function deleteBlogPostById(blogId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/blog/post/${blogId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Delete blog posts
export function publishBlogPostById(blogId: number, published: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/blog/post/${blogId}/publish/${published}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Upload blog photo
export function uploadBlogMedia(blogId: string, file: any, isBanner: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/blog/post/${blogId}/upload/media?isBanner=${isBanner}`, file)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Set a blog of the day
export function setPostOfTheDay(blogId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/blog/post/${blogId}/feature`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
        console.log(error);
      });
  });
}

export async function slackNotifyReport(data: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    fetch('https://hooks.slack.com/services/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', {
      method: 'POST',
      //cors: "no-cors",
      headers: {},
      body: data
    })
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: AxiosError) => {
        reject(error);
      });
  });
}

//Repair now pay later request endpoints

export async function createNewRNPL(rnplDTO: ICreateRNPLDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/rnpl/`, rnplDTO)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function archiveRNPL(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/rnpl/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function getAllRNPL(
  page?: number,
  size?: number,
  countryId?: number,
  from?: string,
  to?: string
): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/rnpl/${`?page=${page}`}${size ? `&size=${size}` : ''}${countryId ? `&countryId=${countryId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function getSingleRNPLCustomer(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/rnpl/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function getAllCustomerRNPL(customerId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/rnpl/customer/${customerId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


export async function updateRNPL(id: string, rnplDTO: ICreateRNPLDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/rnpl/${id}`, rnplDTO)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function reviewRNPLApplication(id: string, reviewStage: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/rnpl/${id}/review?reviewStage=${reviewStage}`, reviewStage)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function approveRNPLApplication(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/rnpl/${id}/approve`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function generateRNPLPayment(id: string, localDate: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/rnpl/${id}/payments/generate?localDate=${localDate}`, localDate)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function getRNPLPayment(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/rnpl/${id}/payments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function rnplPreapproval(
  channel: string,
  identifier: string,
  msisdn: string
): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`payments/rnpl/preapproval?channel=${channel}&identifier=${identifier}&msisdn=${msisdn}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Apply loan to RNPL
export async function applyLoanToRNPL(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/rnpl/${id}/apply-loan`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Generate sticker
export async function generateSticker(
  insurerPolicyNumber: string,
  policyNumber: string,
  serverReference: string,
  stickerNumber: string
): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `admin/policies/${policyNumber}/generate-sticker?insurerPolicyNumber=${insurerPolicyNumber}&policyNumber=${policyNumber}&serverReference=${serverReference}&stickerNumber=${stickerNumber}`
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Admin Expenses endpoints

export async function getAllExpenses(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/expense/`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function createExpense(payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/expense/`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export async function getExpenseById(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/expense/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

{/* Countries Endpoints*/ }

// Get all countries
export async function getAllOperationalCountries(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/settings/countries/`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Create new country
export async function createNewCountry(country_name: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/settings/country?country_name=${country_name}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Reset sale item
export async function resetSaleItem(saleId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/payouts/sale/${saleId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//RGenerate
export async function generateSettlementPayout(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/payouts/${id}/generate-settlement`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


{/* PREMIUM FINANCING */ }

//Get Premium Financing metrics
export async function getPremiumFinancingMetrics(companyId?: number, startDate?: string, endDate?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/insurer/dashboard/metrics/premium-financing?type=metrics${companyId ? `&companyId=${companyId}` : ''}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Get Premium Financing metrics for brokers only
export async function getBrokerPremiumFinancingMetrics(companyId?: number, from?: string, to?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/statistics?companyId=${companyId}&countryId=1${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all Premium Financed applicants
export async function getAllPremiumFinancedApplicants(
  page?: number,
  size?: number,
  countryId?: number,
  from?: string,
  to?: string,
  companyId?: number,
): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/${`?page=${page}`}${size ? `&size=${size}` : ''}${countryId ? `&countryId=${countryId}` : ''}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}${companyId ? `&companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      })
  })
}

// Get all Premium Financed applicants
export async function applyLoanToPremiumFinancing(
  id: string
): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/${id}/apply-loan`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      })
  })
}

//Create new Premium Financed applicant
export async function createNewPremiumFinancedApplicant(premiumApplicant: IPremiumApplicantDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/premium-financing/`, premiumApplicant)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      })
  })
}

//Create new Premium Financing application
export function createNewPremiumFinancing(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/premium-financing/create`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function persistFinancingFromQuote(payload: ISaveQuoteDTO): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/quote/financing/persist`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

//Get a single Premium Financing Application
export async function getSinglePremiumFinancingById(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
  })
}

//Get a single Premium Financing Application
export async function getSinglePremiumFinancingByApplicationNumber(applicationNumber: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/application/${applicationNumber}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error)
      })
  })
}


//Get a single Premium Financing Application
export async function calculateInstallmentFromPremium(premium: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`quote/premium/allInstallments?premium=${premium}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error)
      })
  })
}


//Approve a Premium Financing application
export async function approvePremiumFinancingApplication(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/${id}/approve`)
      .then((response: any) => {
        resolve(response?.data);
      })
  })
}

//Review stage of a Premium Financing application
export async function reviewPremiumFinancingApplication(id: string, reviewStage: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/premium-financing/${id}/review?reviewStage=${reviewStage}`, reviewStage)
      .then((response: any) => {
        resolve(response?.data);
      })
  })
}



//Generate payments for a Premium Financing application
export async function generatePremiumFinancingPayments(id: string, localDate: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/premium-financing/${id}/payments/generate?localDate=${localDate}`, localDate)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Get a Premium Financing application Payments
export async function getPremiumFinancingPayments(id: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/premium-financing/${id}/payments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Run customer payment analysis
export function runPaymentAnalysisOnPremiumFinancing(applicationNumber: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/premium-financing/${applicationNumber}/analyse/payments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export async function runPaymentAnalysis(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/premium-financing/${id}/analyse/payments`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Update a Premium Financing application
export async function updatePremiumFinancingApplication(id: string, premiumApplicant: IPremiumFinancingDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/premium-financing/${id}`, premiumApplicant)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  })
}



// AI VEHICLE VERIFICATION

// Get vehicle verification metrics
export async function getVehicleVerificationMetrics(companyId?: number, startDate?: string, endDate?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/insurer/dashboard/metrics/vehicle-verification?${companyId ? `companyId=${companyId}` : null}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all vehicle verifications
export async function getAllVehicleVerifications(page: number, size: number, companyId?: number, from?: string, to?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/inspection?page=${page}&size=${size}${companyId ? `&companyId=${companyId}` : ''}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Create vehicle verification
export async function createVehicleVerification(payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/inspection`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Update vehicle verification
export async function updateVehicleVerification(id: number, payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/inspection/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get verified vehicle by application number
export async function getVerifiedVehicleById(applicationNumber: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/inspection/application/${applicationNumber}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Export vehicle verification photos
export async function exportVehicleVerificationPhotos(inspectionId: number, vehicleId: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/inspection/${inspectionId}/vehicle/${vehicleId}/export-photos`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Reload vehicle verification photos
export async function reloadVehicleVerificationPhotos(inspectionId: number, vehicleId: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/inspection/${inspectionId}/vehicle/${vehicleId}/reload-photos`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Request vehicle verification photos
export async function requestVehicleVerificationPhotos(inspectionId: number, vehicleId?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/inspection/${inspectionId}/vehicle/request-photos${vehicleId ? `?vehicleId=${vehicleId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Archive a Premium Financing application
export async function archivePremiumFinancingApplication(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/premium-financing/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Get companies by type
export async function getCompaniesByType(companyType: COMPANY_TYPES | undefined): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/teams/types?companyType=${companyType}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get companies portfolio by company id
export async function getCompanyPortfolio(companyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/configurations/portfolio/company/${companyId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get all portfolio
export async function getAllPortfolios(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/configurations/portfolio`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}




// FInANCIER 

//Get Financier Dashboard Disbursement Metrics
export async function getFinancierDashboardDisbursementMetrics(month?: number, year?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/disbursement/metrics?${month ? `month=${month}` : ''}${year ? `&year=${year}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get Financier Dashboard Monthly Bar Chart
export async function getFinancierDashboardMonthlyBarChart(year?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/charts/monthly-bar?${year ? `year=${year}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get Financier Dashboard Defaulting Rate Metrics
export async function getFinancierDashboardDefaultingRateMetrics(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/defaulting-rate`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get Financier Dashboard Monthly Trend Chart
export async function getFinancierDashboardMonthlyTrendChart(startYear?: number, endYear?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/charts/monthly-trend?${startYear ? `startYear=${startYear}` : ''}${endYear ? `&endYear=${endYear}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get Financier Dashboard Product Distribution Chart
export async function getFinancierDashboardProductDistributionChart(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/charts/product-distribution`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get Financier Dashboard Loan Metrics
export async function getFinancierDashboardLoanMetrics(month?: number, year?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/loan/metrics?${month ? `month=${month}` : ''}${year ? `&year=${year}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get Financier Dashboard Metrics Summary
export async function getFinancierDashboardMetricsSummary(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/metrics/summary`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getPremiumFinancingDocuments(applicationNumber: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/premium-financing/${applicationNumber}/documents`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function generatePremiumFinancingDocuments(applicationNumber: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/premium-financing/${applicationNumber}/documents/generate`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get all repayments
export function getAllRepayments(page?: number, size?: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments?page=${page}&size=${size}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get a repayment by id
export function getRepaymentById(repaymentId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/${repaymentId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get a repayment by loan id
export function getRepaymentByLoanId(loanId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/loan/${loanId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Mark a repayment as paid
export function markRepaymentAsPaid(repaymentId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/${repaymentId}/pay`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Calculate and apply penalty for overdue repayment
export function calculateAndApplyPenalty(repaymentId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/${repaymentId}/penalty`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get repayments by customer
export function getRepaymentsByCustomer(customerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/customer/${customerId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get monthly repayment trends
export async function getMonthlyRepaymentTrends(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/financier/dashboard/monthly-repayment-trends`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get all repayments by customer including paid ones
export function getAllRepaymentsByCustomer(customerId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/customer/${customerId}/all`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get repayment for a loan
export function getRepaymentForLoan(loanId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/loan/${loanId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}


// Get Payment History by Reference
export function getLoanPaymentHistoryByReference(reference: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/loans/${reference}/repayments`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Get Payment History by Reference
export function regeneratePaymentHistory(reference: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/loans/${reference}/generate-repayments`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
//Get total outstanding amount for a loan
export function getTotalOutstandingAmountForLoan(loanId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/loan/${loanId}/outstanding`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get loan repayment status
export function getLoanRepaymentStatus(loanId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/loan/${loanId}/status`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get all overdue repayments
export function getAllOverdueRepayments(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/repayments/overdue`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}


{/* Broker Dashboard endpoints */ }

// Broker Dashboard Metrics
export async function getBrokerDashboardBrokerPerformance(companyId?: number, period?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/broker/dashboard/metrics/broker-performance?${companyId ? `companyId=${companyId}` : ''}${period ? `&period=${period}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data?.body?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get monthly broker metrics
export async function getBrokerDashboardMonthlyMetrics(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/broker/dashboard/metrics/monthly`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get summary of all broker metrics
export async function getBrokerDashboardMetricsSummary(year?: string, companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/broker/dashboard/metrics/summary?year=${year}${companyId ? `&companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get insurance company distribution metrics
export async function getBrokerDashboardInsurerDistribution(companyId?: number, month?: string, year?: number, startDate?: string, endDate?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/broker/dashboard/chart/insurer-distribution?${companyId ? `companyId=${companyId}` : ''}${month ? `&month=${month}` : ''}${year ? `&year=${year}` : ''}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get insurance company distribution metrics
export async function getBrokerDashboardInsuranceMetrics(companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/metrics/insurance${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get insurance company distribution metrics
export async function getBrokerDashboardCustomerRetentionMetrics(companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/metrics/customer`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}



// Get insurance company distribution metrics
export async function getBrokerDashboardNonMotorPolicyData(startDate?: string, endDate?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/metrics/non-motor-policy-statistics?type=non-motor${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get insurance company distribution metrics
export async function getBrokerDashboardRenewalsSummary(year: number, month?: number, insurerId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/renewals/summary${year ? `?year=${year}` : ''}${insurerId ? `&insurerId=${insurerId}` : ''}${month ? `&month=${month}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get insurance company distribution metrics
export async function getListOfExpiringPolicies(page: number, size: number, renewalState: string, insurerId?: string, expiringTo?: string, expiringFrom?: string, month?: number, year?: number, search?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/metrics/expiring-policies?page=${page}&size=${size}&renewalState=${renewalState}${insurerId ? `&insurerId=${insurerId}` : ''}${expiringTo ? `&expiringTo=${expiringTo}` : ''}${expiringFrom ? `&expiringFrom=${expiringFrom}` : ''}${month ? `&month=${month}` : ``}${year ? `&year=${year}` : ``}${search ? `&search=${search}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get revenue trends with target
export async function getRevenueTrendWithTarget(companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/metrics/revenue-trends${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get policy distribution by protection type
export async function getBrokerDashboardProtectionTypes(companyId?: number, startDate?: string, endDate?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/broker/dashboard/chart/protection-types?${companyId ? `companyId=${companyId}` : ''}${startDate ? `&startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get monthly sales and commission metrics
export async function getBrokerDashboardChartMonthlyMetrics(year?: number, companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    const params = [
      year ? `year=${year}` : '',
      companyId ? `companyId=${companyId}` : ''
    ].filter(Boolean).join('&');
    api.get(`admin/broker/dashboard/chart/monthly-metrics${params ? `?${params}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get broker dashboard staff performance metrics
export async function getBrokerDashboardStaffPerformance(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/broker/dashboard/performance`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Mark policy as renewed
export async function markPolicyAsRenewed(policyNumber: string, renewedPolicyNumber?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.patch(`admin/policies/${policyNumber}/mark-renewed${renewedPolicyNumber ? `?renewedPolicyNumber=${renewedPolicyNumber}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}



//INSURER DASHBOARD

export async function getInsurerDashboardMetricsSummary(companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/insurer/dashboard/metrics/summary${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getSingleInsurerDashboardMetrics(companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/insurer/dashboard/metrics/insurer${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getPolicyDistribution(companyId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/insurer/dashboard/chart/protection-type-distribution${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

{/* BRANCHES ENDPOINTS */ }
//Get all branches
export async function getAllBranches(pageNumber: number, pageSize?: number, active?: boolean, companyId?: number, name?: string, offset?: number, paged?: boolean,): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/branches?pageNumber=${pageNumber}&pageSize=${pageSize}${active ? `&active=true` : ''} ${name ? `&name=${name}` : ''}${companyId ? `&companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get active branches by company
export async function getActiveBranchesByCompany(companyId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/branches/company/${companyId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Create new branch
export async function createBranch(payload: IBranch): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`admin/branches`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Update branch
export async function updateBranch(payload: IBranch, id: number, managerId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.put(`admin/branches/${id}${managerId ? `?managerId=${managerId}` : ''}`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Delete branch
export async function deleteBranch(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`admin/branches/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Search branch by name
export async function searchBranchByName(name: string | undefined): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/branches/search${name ? `?name=${name}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

{/*Revenue Target endpoints*/ }

//Set revenue target
export async function setRevenueTarget(companyId: number, payload: ICreateRevenueTargetDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`admin/revenue-targets/companies${companyId ? `?companyId=${companyId}` : ''}`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get revenue target
export async function getRevenueTarget(month: string, year: number, companyId?: number, branchId?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/revenue-targets/companies?${month ? `month=${month}` : ''}${year ? `&year=${year}` : ''}${companyId ? `&companyId=${companyId}` : ''}${branchId ? `&branchId=${branchId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get all revenue targets
export async function getAllRevenueTargets(companyId: number, year: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/revenue-targets/companies/${companyId}/year/${year}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Delete revenue target
export async function deleteRevenueTarget(targetId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`admin/revenue-targets/${targetId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get branch by companyId
export async function getBranchByCompanyId(companyId: number): Promise<void> {

  return new Promise<any>((resolve, reject) => {
    api.get(`admin/branches/company/${companyId}`)
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: Error) => {
        reject(error)
      })
  })
}

//Get branch performance
interface IBranchPerformance {
  companyId?: number;
  compareEndDate: string;
  compareStartDate?: string;
  startdate: string;
  endDate: string;
}

export async function getBranchPerformance(
  startDate: string,
  endDate: string,
  companyId?: number,
  compareEndDate?: string,
  compareStartDate?: string,
): Promise<void> {

  return new Promise<any>((resolve, reject) => {
    api.get(`admin/insurer/dashboard/chart/branch-performance${startDate ? `?startDate=${startDate}` : ''}${endDate ? `&endDate=${endDate}` : ''}${companyId ? `&companyId=${companyId}` : ''}${compareStartDate ? `&compareStartDate=${compareStartDate}` : ''}${compareEndDate ? `&compareEndDate=${compareEndDate}` : ''}`)
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: Error) => {
        reject(error)
      })
  })
}

{/* Export Renewals */ }
export async function exportPolicyRenewals(brokerId?: number, search?: string, expiredFrom?: string, expiredTo?: string, insurerId?: number, policyType?: string, renewalState?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/broker/dashboard/export${brokerId ? `?brokerId=${brokerId}` : ''}${search ? `&search=${search}` : ''}${expiredFrom ? `&expiredFrom=${expiredFrom}` : ''}${expiredTo ? `&expiredTo=${expiredTo}` : ''}
      ${insurerId ? `&insurerId=${insurerId}` : ''}${policyType ? `&policyType=${policyType}` : ''}${renewalState ? `&renewalState=${renewalState}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: Error) => {
        reject(error)
      })
  })
}





//Get intermediaries
export async function getAllIntermediaries(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/intermediaries`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function createIntermediary(payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`admin/intermediaries`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getIntermediaryById(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/intermediaries/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function updateIntermediary(id: number, payload: IIntermediaryDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.put(`admin/intermediaries/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function sendMIDaccessRequestByEmail(id: number, email: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`admin/intermediaries/request-mid-access?insurerEmail=${email}&intermediaryId=${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function deleteIntermediary(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`admin/intermediaries/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getIntermediariesByBroker(brokerId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/intermediaries/broker/${brokerId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getIntermediariesByInsurer(insurerId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/intermediaries/insurer/${insurerId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function searchIntermediaries(name: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/intermediaries/search${name ? `?name=${name}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getIntermediaryMotorPolicies(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/intermediaries/${id}/motor-policies`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function saveIntermediaryMotorPolicies(id: number, policies: Array<{ id?: number; name: string; rate: number | null }>): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.put(`admin/intermediaries/${id}/motor-policies`, policies)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function deleteIntermediaryMotorPolicy(policyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`admin/intermediaries/motor-policies/${policyId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getInsurerContacts(id: number | string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/insurers/${id}/contacts`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function saveInsurerContacts(id: number | string, contacts: Array<{ id?: number; name: string; phone: string; email: string; role: string }>): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.put(`admin/insurers/${id}/contacts`, contacts)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function deleteInsurerContact(contactId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`admin/insurers/contacts/${contactId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getNotificationContacts(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/settings/notifications/contacts`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getNotificationContactsByCompanyId(companyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/settings/notifications/contacts/${companyId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function saveNotificationContacts(contacts: Array<{ id?: number; email: string; phone: string }>): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.put(`admin/settings/notifications/contacts`, contacts)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function deleteNotificationContact(contactId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`admin/settings/notifications/contacts/${contactId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export interface IBrokerActivation {
  companyId: number
  companyName: string
  companyType: string
  companyEmail?: string
  companyPhone?: string
  ownerStaffId: number
  ownerFirstName: string
  ownerLastName: string
  ownerEmail: string
  ownerPhone?: string
  ownerJobTitle?: string
  registeredOn?: string
  lastUpdatedOn?: string
  activated: boolean
  passwordSet: boolean
}

export async function listBrokers(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/brokers`)
      .then((response: any) => resolve(response?.data))
      .catch((error: any) => reject(error))
  })
}

export async function listPendingBrokers(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/brokers/pending`)
      .then((response: any) => resolve(response?.data))
      .catch((error: any) => reject(error))
  })
}

export async function activateBroker(companyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.post(`admin/brokers/${companyId}/activate`)
      .then((response: any) => resolve(response?.data))
      .catch((error: any) => reject(error))
  })
}

export async function deactivateBroker(companyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.post(`admin/brokers/${companyId}/deactivate`)
      .then((response: any) => resolve(response?.data))
      .catch((error: any) => reject(error))
  })
}

export async function getAdminUnreadNotifications(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/notifications/unread`)
      .then((response: any) => resolve(response?.data))
      .catch((error: any) => reject(error))
  })
}

export async function getAdminNotifications(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/notifications`)
      .then((response: any) => resolve(response?.data))
      .catch((error: any) => reject(error))
  })
}

export async function approveMIDAccess(apiKey: string, intermediaryCode: string, branchCode: string, approvalToken: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/public/intermediary/approve-mid-access?apiKey=${apiKey}&intermediaryCode=${intermediaryCode}&branchCode=${branchCode}&approvalToken=${approvalToken}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}


//Enums

export async function getAllEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/enums/all`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getAccountProviderEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/account-providers`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getAccountTypeEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/account-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}


export async function getCompanyTypeEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/company-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getEmploymentTypeEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/employment-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getGenderEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/genders`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getIdTypeEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/id-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getInstallmentPeriodEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/installment-periods`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getPaymentStatusEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/payment-statuses`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getPolicyTypeEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/policy-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getProtectionTypeEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/protection-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getUsageGroupEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/usage-groups`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getVehicleUsageEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/vehicle-usages`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}


export async function getVehicleUsesEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/vehicle-uses`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getRiskCategoriesEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/risk-categories`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getPortfoliosEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/portfolios`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getClassOfBusinessEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/class-of-businesses`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

export async function getChargetypesEnums(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`enums/charge-types`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}



{/* COMMISSIONS DASHBOARD*/ }

// Get commission distribution by insurer
export async function getCommissionDistributionByInsurer(companyId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/commission-charts/by-insurer${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get commission distribution by protection type
export async function getCommissionDistributionByProtectionTypeByCompanyId(companyId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/commission-charts/by-protection-type${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get commission distribution by policy type
export async function getCommissionDistributionByPolicyTypeByCompanyId(companyId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/commission-charts/by-policy-type${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get all commission chart data
export async function getAllCommissionChartDataByCompanyId(companyId: number, from?: string, to?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/commission-charts/dashboard${companyId ? `?companyId=${companyId}` : ''}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get commission metrics
export async function getCommissionMetricsByCompanyId(companyId: number, insurerId: number, policyType: string, from?: string, to?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/commission-charts/metrics${companyId ? `?companyId=${companyId}` : ''}${insurerId ? `&insurerId=${insurerId}` : ''}${policyType ? `&policyType=${policyType}` : ''}${from ? `&from=${from}` : ''}${to ? `&to=${to}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get monthly commission trends
export async function getMonthlyCommissionTrends(companyId: number, year: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`admin/commission-charts/monthly-trend?tp=commission${companyId ? `?companyId=${companyId}` : ''}${year ? `&year=${year}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Send paystack link by sms
export async function sendPaystackLinkBySMS(id: number, medium: string, destination: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payments/${id}/share/${medium}/${destination}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}
//Send setting notifications
export async function sendSettingNotifications(payload: ITeamNotification): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`/admin/teams/notification-settings`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}
//Get setting notifications
export async function getSettingNotifications(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/teams/notification-settings`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

{/* Bill Management */ }

// Get all bills
export async function getAllBills(companyId: number, pageNumber: number, pageSize: number,): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills?companyId=${companyId}&pageNumber=${pageNumber}&pageSize=${pageSize}&sort.sorted=true&sort.unsorted=false`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Create a new bill
export async function createBill(payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`/admin/bills`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get a bill by ID
export async function getBillById(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Update an existing bill
export async function updateBill(id: number, payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.put(`/admin/bills/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Delete a bill
export async function deleteBill(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.delete(`/admin/bills/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Partially update a bill
export async function partiallyUpdateBill(id: number, payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.patch(`/admin/bills/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Mark bill as paid
export async function markBillAsPaid(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.put(`/admin/bills/${id}/mark-paid`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Mark bill as paid
export async function markBillAsPaidWithDetails(id: number, payload: any): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.put(`/admin/bills/${id}/mark-paid-with-details`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Mark bill as unpaid
export async function markBillAsUnpaid(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.put(`/admin/bills/${id}/mark-unpaid`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get bills by amount range
export async function getBillsByAmountRange(amountRange: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/amount-range?amountRange=${amountRange}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get bills by company
export async function getBillsByCompany(companyId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/company/${companyId}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get bills by date range
export async function getBillsByDateRange(dateRange: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/date-range?dateRange=${dateRange}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get bills by month
export async function getBillsByMonth(month: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/month/${month}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get overdue bills
export async function getOverdueBills(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/overdue`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get a bill by reference number
export async function getBillByReferenceNumber(referenceNumber: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/reference/${referenceNumber}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get bill statistics
export async function getBillStatisticsByCompanyId(companyId: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/statistics${companyId ? `?companyId=${companyId}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// Get unpaid bills
export async function getUnpaidBills(): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/bills/unpaid`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Commission and Insurers payouts

//get all commissions payouts
export async function getAllCommissionPayouts(companyId?: number, page?: number, size?: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payouts/commission${companyId ? `?companyId=${companyId}` : ''}${page ? `&page=${page}` : ''}${size ? `&size=${size}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//get commission payouts by its ID
export async function getAllCommissionPayoutsById(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payouts/commission/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Get all insurers payouts
export async function getAllInsurerPayouts(page?: number, size?: number, companyId?: number, brokerId?: number, fromDate?: string, toDate?: string, isPaid?: string, isApproved?: boolean, reviewStage?: string, reference?: string, signal?: AbortSignal): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payouts?page=${page}&size=${size}${companyId ? `&companyId=${companyId}` : ''}${brokerId ? `&brokerId=${brokerId}` : ''}${fromDate ? `&fromDate=${fromDate}` : ''}${isApproved ? `&isApproved=${isApproved}` : ''}${isPaid ? `&isPaid=${isPaid}` : ''}${reviewStage ? `&reviewStage=${reviewStage}` : ''}${toDate ? `&toDate=${toDate}` : ''}${reference ? `&reference=${reference}` : ''}`, { signal })
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//get insurer payout by its ID
export async function getAllInsurerPayoutsById(id: number): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payouts/${id}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//Complete a payout
export async function completePayout(id: number, payload: completePayoutDTO): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.post(`/admin/payouts/${id}/complete`, payload)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

//get comprehensive payout statistics
export async function getPayoutStatistics(id: number, type?: string): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payouts/statistics?companyId=${id}${type ? `&type=${type}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}
// GET PAYOUT BY COMPANY ID
export async function getPayoutByCompany(companyId: number, isPaid?: boolean): Promise<void> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/payouts/companies/${companyId}/payouts${isPaid ? `?isPaid=${isPaid}` : ''}`)
      .then((response: any) => {
        resolve(response?.data)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

// GET SALES BY PAYOUT ID
export function getSalesByPayoutId(payoutId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payouts/${payoutId}/sales`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}



{/* Non - Motor Policy Management */ }

// Create new non-motor policy
export function createNonMotorPolicy(payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/non-motor-policies`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Update non-motor policy
export function updateNonMotorPolicy(policyNumber: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/non-motor-policies/${policyNumber}`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Delete non-motor policy
export function deleteNonMotorPolicy(policyId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/non-motor-policies/${policyId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


// ASSIGN NON-MOTOR POLICY TO MANAGER
export function assignNonMotorPolicyManager(policyNumber: string, staffId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/${policyNumber}/assign-to/${staffId}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

// Update policy review stage
export function updateNonMotorPolicyReviewStage(policyId: number, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/non-motor-policies/${policyId}/review-stage`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Update policy status
export function updateNonMotorPolicyStatus(policyId: number, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/non-motor-policies/${policyId}/status`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policy by policy number
export function getNonMotorPolicyByPolicyNumber(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all active non-motor policies
export function getAllActiveNonMotorPolicies(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/active`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all non-motor policies
export function getAllNonMotorPolicies(page: number, size: number, active?: string, brokerId?: number, searchParam?: string, insurerId?: number, policyType?: string, reviewStage?: string, managerId?: number, expiredFrom?: string, expiredTo?: string, startsOn?: string, endsOn?: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries?page=${page}&size=${size}${active ? `&active=${active || false}` : ''}${brokerId ? `&brokerId=${brokerId}` : ''}${searchParam ? `&searchParam=${searchParam}` : ''}${insurerId ? `&insurerId=${insurerId}` : ''}${policyType ? `&policyType=${policyType}` : ''}${reviewStage ? `&reviewStage=${reviewStage}` : ''}${managerId ? `&managerId=${managerId}` : ''}${startsOn ? `&startsOn=${startsOn}` : ''}${endsOn ? `&endsOn=${endsOn}` : ''}${expiredFrom ? `&expiredFrom=${expiredFrom}` : ''}${expiredTo ? `&expiredTo=${expiredTo}` : ''}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function getAllBrokerPoliciesCount(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/teams/portfolio`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policies by broker
export function getNonMotorPoliciesByBroker(brokerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/broker/${brokerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policies by category
export function getNonMotorPoliciesByCategory(category: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/category/${category}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policies by customer
export function getNonMotorPoliciesByCustomer(customerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/customer/${customerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all expired non-motor policies
export function getAllExpiredNonMotorPolicies(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/expired`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Delete non-motor policies by policyNumber
export function deleteSingleNonMotorPolicy(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .delete(`/admin/non-motor-policies/${policyNumber}`)
      .then((response: any) => {
        resolve(response);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get policies expiring within date range
export function getNonMotorPoliciesExpiringWithinDateRange(from: string, to: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/expiring?from=${from}&to=${to}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get all non-motor policy types
export function getAllNonMotorPolicyTypes(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/policy-types`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get policy types by category
export function getNonMotorPolicyTypesByCategory(category: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/policy-types/category/${category}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policy statistics
export function getNonMotorPolicyStatistics(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/statistics`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get comprehensive non-motor policy statistics
export function getComprehensiveNonMotorPolicyStatistics(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/statistics/comprehensive`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policy summary by policy number
export function getNonMotorPolicySummaryByPolicyNumber(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries/${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get active non-motor policy summaries by broker
export function getActiveNonMotorPolicySummariesByBroker(brokerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries/active/broker/${brokerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policy summaries by broker
export function getNonMotorPolicySummariesByBroker(brokerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries/broker/${brokerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policy summaries by customer
export function getNonMotorPolicySummariesByCustomer(customerId: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries/customer/${customerId}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policy summaries by type
export function getNonMotorPolicySummariesByType(policyType: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries/type/${policyType}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Get non-motor policies by type
export function getNonMotorPoliciesByType(policyType: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/type/${policyType}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Search non-motor policy summaries by customer name
export function searchNonMotorPolicySummariesByCustomerName(customerName: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/summaries/search?customerName=${customerName}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Sync specific non-motor policy summary
export function syncNonMotorPolicySummary(policyNumber: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/sync/${policyNumber}`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Sync all non-motor policy summaries
export function syncAllNonMotorPolicySummaries(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/non-motor-policies/sync/bulk`)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

// Complete non-motor policy payment
export function completeNonMotorPolicyPayment(policyNumber: string, payload: any): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/non-motor-policies/policy-number/${policyNumber}/complete-payment`, payload)
      .then((response: any) => {
        resolve(response?.data?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}





{/*Admin configurations */ }

// Get active risk types
export function getActiveRiskTypes(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/configurations/risk-types/active`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


//Creates a new risk type
export function createNewRiskType(payload: IRiskTypeInsuranceConfiguration): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/configurations/risk-types`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Creates a new motor insurance configuration
export function createNewMotorInsuranceConfiguration(payload: IInsuranceType): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/configurations/motor-insurance`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Edits a new motor insurance configuration
export function editMotorInsuranceConfiguration(payload: IInsuranceType): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/configurations/motor-insurance`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//get a motor insurance configuration by id
export function getMotorInsuranceConfigurationById(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/motor-insurance/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//edits a motor insurance configuration by id
export function updateMotorInsuranceConfigurationById(id: number, payload: IInsuranceType): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/configurations/motor-insurance/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//get all motor insurance configurations
export function getAllMotorInsuranceConfigurations(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/motor-insurance`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//delete motor insurance configurations by id
export function udptadeMotorInsuranceConfigurationStatus(id?: number, active?: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/configurations/motor-insurance/${id}/status?active=${active}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//Creates a new insurance product

export function createNewInsurancePortfolio(payload: ICoverageTypeConfiguration): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/configurations/portfolio`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//get list of portfolios

export function getListOfPortfolios(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/portfolio`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//get a portfolio by id

export function getPortfolioById(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/portfolio/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//edit a portfolio by id

export function updatePortfolioById(id: number, payload: ICoverageTypeConfiguration): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/configurations/portfolio/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//delete portfolio by id

export function updatePortfolioStatus(id?: number, activate?: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/configurations/portfolio/${id}/status?active=${activate}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


//creates no claim discount configurations
export function createNoClaimDiscount(payload: IDiscountRate): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .post(`admin/configurations/no-claim-discount`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//gets all no claim discount configurations
export function getAllNoClaimDiscount(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/no-claim-discount`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//gets a no claim discount by id
export function getlNoClaimDiscountById(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/no-claim-discount/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//update no claim discount configuration by id
export function updateNoClaimDiscount(id: number, payload: IDiscountRate): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/configurations/no-claim-discount/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//delete no claim discount configuration by id
export function updateNoClaimDiscountStatus(id?: number, activate?: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/configurations/no-claim-discount/${id}/status?active=${activate}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//gets all risk types 
export function getAllRiskTypes(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/risk-types`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//gets a risk type by id
export function getRiskType(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/configurations/risk-types/${id}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

//updates a risk type by id
export function updateRiskType(id: number, payload: IRiskTypeInsuranceConfiguration): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`admin/configurations/risk-types/${id}`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}


//delete risk types 
export function updateRiskTypeStatus(id?: number, activate?: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`admin/configurations/risk-types/${id}/status?active=${activate}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });

}

export function getVehicleBodyTypesForInsuranceConfiguration(): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`vehicle/body-types`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });

}


export function getPaymentLinkForPremiumFinancing(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/payments/${id}/generate-link`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });

}

export function agreeToTermsAndConditions(accepted: boolean): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`admin/teams/terms-and-conditions?accepted=${accepted}`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function approveSingleLoan(id: number): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .put(`/admin/loans/${id}/approve`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function rejectSingleLoan(id: number, payload: { reason: string }): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .patch(`/admin/loans/${id}/reject`, payload)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function generateLoanAgreementDocuments(applicationId: string): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api.get(`/admin/loans/${applicationId}/generate-docs`)
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

export function submitPaymentReceipt(id: number | null, payload: IEditPaymentDTO, evidence: any) {
  return new Promise<any>((resolve, reject) => {
    api
      .put(
        `/admin/payments/${id}?bank=${payload.bank ? `${payload.bank}` : ''}&channel=${payload.channel
        }&dateOfPayment=${payload.dateOfPayment}&dueDate=${payload.dueDate}&narration=${payload.narration
        }&processor=${payload.processor}&reference=${payload.reference}&timeOfPayment=${payload.timeOfPayment
        }`,
        evidence
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}

export function insurerIssuanceMetrics(from: string, to: string) {
  return new Promise<any>((resolve, reject) => {
    api
      .get(
        `admin/metrics/insurer-issuance-mode?from=${from}&to=${to}`
      )
      .then((response: any) => {
        resolve(response?.data);
      })
      .catch((error: Error) => {
        reject(error);
      });
  });
}
// ============================================================================
// Broker Mapping Tool — broker upload + internal listing + import-mapped
// ============================================================================

export function uploadBrokerRawFile(file: File): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/broker/mapping-tool/uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response: any) => resolve(response?.data))
      .catch((error: AxiosError) => reject(error));
  });
}

export function listBrokerOwnUploads(params: {
  page?: number;
  size?: number;
}): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/broker/mapping-tool/uploads`, {
        params: {
          page: params.page ?? 0,
          size: params.size ?? 20,
        },
      })
      .then((response: any) => resolve(response?.data))
      .catch((error: AxiosError) => reject(error));
  });
}

// Absolute URL the broker can <a href> to download the mapped file
// produced by the internal team.
export function brokerDownloadMappedUrl(uploadId: number): string {
  return `${apiUrl}/api/broker/mapping-tool/uploads/${uploadId}/download-mapped`;
}

export function listBrokerRawUploadsForInternal(params: {
  status?: string;
  page?: number;
  size?: number;
}): Promise<any> {
  return new Promise<any>((resolve, reject) => {
    api
      .get(`/admin/mapping-tool/uploads`, {
        params: {
          status: params.status,
          page: params.page ?? 0,
          size: params.size ?? 20,
        },
      })
      .then((response: any) => resolve(response?.data))
      .catch((error: AxiosError) => reject(error));
  });
}

// Absolute URL internal users <a href> to download the raw file.
export function brokerRawUploadDownloadUrl(uploadId: number): string {
  return `${apiUrl}/api/admin/mapping-tool/uploads/${uploadId}/download`;
}

export function importMappedFileForUpload(
  uploadId: number,
  file: File
): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/mapping-tool/uploads/${uploadId}/import-mapped`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response: any) => resolve(response?.data))
      .catch((error: AxiosError) => reject(error));
  });
}

/**
 * Internal-direct bulk upload. Used when a broker emails the mapped policy
 * sheet to internal staff — no prior broker raw upload exists. Internal
 * staff picks the broker and uploads the mapped CSV in one step; the
 * backend creates a synthetic BrokerRawUpload (status=MAPPED,
 * internalDirect=true) and runs the same import pipeline as the
 * broker-then-internal flow.
 */
export function directImportForBroker(
  brokerCompanyId: number,
  file: File
): Promise<any> {
  const formData = new FormData();
  formData.append('brokerCompanyId', String(brokerCompanyId));
  formData.append('file', file);
  return new Promise<any>((resolve, reject) => {
    api
      .post(`/admin/mapping-tool/uploads/direct`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response: any) => resolve(response?.data))
      .catch((error: AxiosError) => reject(error));
  });
}
